import { useEffect, useState } from "react";
import axios from 'axios';
import styled from "styled-components";
import fundo from "../../../../styles/fundo.svg";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaDownload } from "react-icons/fa";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";


import logo1 from "./logo-1.png";
import logo6 from "./logo-6.png";


// ========== LOOK & FEEL MINIMALISTA ==========
const palette = {
  // fundo da página (se usar)
  cardBg: "#ffffff",
  headBg: "#0a7a3f",          // cabeçalho da tabela
  headText: "#ffffff",
  text: "#334155",
  textMuted: "#64748b",
  border: "#e5e7eb",
  zebra: "#f8fafc",

  accent: "#0ea5e9",
};

// Container do card/tabela
const Geral = styled.div`
  overflow: hidden;
  background: ${palette.cardBg};
  border: 1px solid ${palette.border};
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  margin: 10px auto;
  width: 100%;

  /* Envolve a tabela num scroller vertical suave */
  & > div {
    overflow: auto;
  }

  /* Scrollbar minimal */
  & > div::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  & > div::-webkit-scrollbar-track {
    background: transparent;
  }
  & > div::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 999px;
  }
  & > div::-webkit-scrollbar-thumb:hover {
    background: #c4c9d1;
  }

  @media (max-width: 1367px) {
    margin: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  }

  @media (max-width: 1024px) {
    margin: 8px;
    margin-bottom: 100px;
  }
`;

// Tabela
const Table = styled.table`
  border-collapse: collapse;   /* 👈 junto com as bordas fica linear */
  width: 100%;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto,
    "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif;


  /* borda inferior de cada linha do corpo */
  tbody tr:not(:last-child) td {
    border-bottom: 1px solid ${palette.border};
  }
`;

// Cabeçalho sticky com fallback de cor
const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${palette.headBg};
  background-blend-mode: multiply;
  backdrop-filter: saturate(120%) blur(2px);
`;

// Linhas
export const Tr = styled.tr`
  /* ❌ remova esta linha:
  border-bottom: 1px solid ${palette.border};
  */

  ${({ noHover }) =>
    !noHover &&
    `
    &:hover {
      background-color: ${palette.zebra}; /* ou outra cor de hover */
    }
  `}

  @media (max-width: 1024px) {
    .hide-on-mobile {
      display: none;
    }
  }
`;

// Cabeçalho das colunas
export const Th = styled.th`
  color: ${palette.headText};
  padding: clamp(8px, 1.6vw, 14px) clamp(6px, 1.4vw, 10px);
  font-size: clamp(8px, 1.2vw, 11px);
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  white-space: nowrap;

  &.hide-on-mobile {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .tooltip {
    position: relative;
    cursor: help;
  }
  .tooltip .tooltiptext {
    visibility: hidden;
    width: max-content;
    background-color: #111827;
    color: #fff;
    text-align: left;
    border-radius: 8px;
    padding: 4px 8px;
    position: absolute;
    z-index: 10;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity .2s ease;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(0,0,0,.2);
  }
  .tooltip:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
  }
`;

// Células
export const Td = styled.td`
  padding: clamp(10px, 1.8vw, 16px) clamp(6px, 1.4vw, 10px);
  text-align: ${(props) => (props.alignCenter ? 'center' : 'left')};
  font-size: clamp(8px, 1.4vw, 11px);
  color: ${palette.text};
  background-color: ${(props) => (props.zebra ? palette.zebra : "transparent")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.mobile-hide {
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

// Corpo
export const Tbody = styled.tbody`
  background-color: #fff;
`;

// TH que funciona como “cabeçalho-título” (linha 1)
export const CabecalhoTabela = styled.th`
  text-align: center;
  padding: clamp(10px, 2vw, 18px);
  color: ${palette.headText};
  background-color: ${palette.headBg};
  position: sticky;
  top: 0;
  z-index: 6; /* acima do Thead */
  border-bottom: 1px solid rgba(255,255,255,0.08);

  h2 {
    margin: 0;
    font-size: clamp(14px, 2.4vw, 18px);
    font-weight: 700;
    letter-spacing: 0.01em;
  }
`;

// Botão “fantasma” no header (usado para ícones)
const Button = styled.td`
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: transparent;
  color: ${palette.headText};
  opacity: .9;
  transition: transform .12s ease, opacity .12s ease;
  user-select: none;

  &:hover { transform: translateY(-1px); opacity: 1; }
  &:active { transform: translateY(0); opacity: .85; }

  @media (max-width: 780px) {
    font-size: 14px;
  }
`;

// Header do título/ações
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 780px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;

    h2 { font-size: clamp(16px, 5vw, 20px); }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  p {
    margin: 0;
    font-size: 12px;
    color: ${palette.headText};
    opacity: .85;
  }

  @media (max-width: 780px) {
    width: 100%;
    justify-content: space-between;
    p { font-size: 11px; }
  }
`;

const IconGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  /* alvo o <td> Button que contém ícones */
  ${Button} {
    padding: 6px 8px;
    border-radius: 10px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
  }
`;



const useBoletim = (tipo, urlApi, rota = "atualiza-qualidade", periodo = null) => {
  const [dados, setDados] = useState([]);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/${tipo}`);

        const ordem = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];

        // 🔒 Verifica se é array antes de tentar ordenar
        if (Array.isArray(res.data)) {
          const dadosOrdenados = res.data.sort((a, b) => {
            return ordem.indexOf(a.PERIODO) - ordem.indexOf(b.PERIODO);
          });
          setDados(dadosOrdenados);
        } else {
          console.warn(`⚠️ Dados inesperados recebidos para tipo ${tipo}:`, res.data);
          setDados([]); // ou setDados([res.data]) se for um único objeto válido
        }

      } catch (error) {
        console.error(`Erro ao buscar ${tipo}:`, error);
      }
    };

    fetchDados();
  }, [tipo, urlApi, rota, periodo]);

  return dados;
};
const useHoraRodou = (urlApi, rota = "atualiza-qualidade") => {
  const [hora, setHora] = useState(null);

  useEffect(() => {
    const fetchHora = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/hora-rodou`);
        setHora(res.data.hora); // já pega a string diretamente
      } catch (error) {
        console.error("Erro ao buscar hora-rodou:", error);
      }
    };

    fetchHora();
  }, [urlApi, rota]);

  return hora;
};

const Grid = ({ urlApi, periodos, zoomIn, zoomOut }) => {
  const horaRodou = useHoraRodou(urlApi);


  const atr = useBoletim("qua-atr", urlApi);
  const atrCooperval = useBoletim("qua-atr-cooperval", urlApi);
  const atrParceiro = useBoletim("qua-atr-parceiro", urlApi);
  const fibraCana = useBoletim("qua-fibra-cana", urlApi);
  const pureza = useBoletim("qua-pureza", urlApi);
  const impureza = useBoletim("qua-impureza-mineral-geral", urlApi);
  const impurezaManual = useBoletim("qua-impureza-mineral-manual", urlApi);
  const impurezaMecanizada = useBoletim("qua-impureza-mineral-mecanizada", urlApi);

  const impurezaVegetalGeral = useBoletim("qua-impureza-vegetal-geral", urlApi);
  const impurezaVegetalManual = useBoletim("qua-impureza-vegetal-manual", urlApi);
  const impurezaVegetalMecanizada = useBoletim("qua-impureza-vegetal-mecanizada", urlApi);

  const arCana = useBoletim("qua-ar-cana", urlApi);
  const brixCana = useBoletim("qua-brix-cana", urlApi);
  const pcc = useBoletim("qua-pcc", urlApi);

  //Preenchendo o title



  //=======================JUNTA OS MAP======================================//
  const isMobile = window.innerWidth <= 768;
  const codigosPeriodos = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
  const periodosMobile = ['d1', 'd', 'semana', 'mes', 'safra'];

  const LinhaTabela = ({ titulo, dados2 }) => {
    const periodosEmNegrito = ['d', 'semana', 'mes', 'safra'];

    const dadosFiltrados = isMobile
      ? dados2.filter(item => periodosMobile.includes(item.PERIODO))
      : dados2;

    return (
      <Tr>
        <Td><strong>{titulo}</strong></Td>
        {dadosFiltrados.map((item, index) => {
          const valorFormatado = item.VALOR != null && item.VALOR !== 0
            ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : "0";

          const estiloFonte = periodosEmNegrito.includes(item.PERIODO)
            ? { fontWeight: 'bold' }
            : {};

          return (
            <Td key={`2-${index}`} alignCenter zebra={index % 2 === 0} style={estiloFonte}>
              {valorFormatado}
            </Td>
          );
        })}
      </Tr>
    );
  };



  //=======================GERAR PDF==========================================//

  const getBase64FromImage = (imgSrc) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({ base64: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
      };

      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = imgSrc;
    });
  };






  //===========================EXPORTAR PDF==============================//
  const gerarPdfRelatorio = async () => {
    const isMobile = window.innerWidth <= 768;
    const periodosOrdenados = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
    const periodosMobile = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
    const periodosFiltrados = isMobile ? periodosMobile : periodosOrdenados;

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });

    // Marca d'água (logo1 sempre)
    const { base64: marcaDaguaBase64, width: mw, height: mh } = await getBase64FromImage(logo1);
    const maxMarcaW = 400;
    const maxMarcaH = 300;
    let marcaW = mw, marcaH = mh;
    if (mw > maxMarcaW || mh > maxMarcaH) {
      const ratio = Math.min(maxMarcaW / mw, maxMarcaH / mh);
      marcaW = mw * ratio;
      marcaH = mh * ratio;
    }
    const centerX = (842 - marcaW) / 2;
    const centerY = (595 - marcaH) / 2;

    // Cabeçalho fixo
    const { base64, width, height } = await getBase64FromImage(logo6);
    let displayW = width, displayH = height;
    const maxW = 200, maxH = 60;
    if (width > maxW || height > maxH) {
      const ratio = Math.min(maxW / width, maxH / height);
      displayW = width * ratio;
      displayH = height * ratio;
    }

    doc.addImage(base64, 'PNG', 20, 10, displayW, displayH);
    doc.setFontSize(12);
    doc.setTextColor(87, 87, 87);
    doc.text("Boletim Qualidade", 30, 70);
    doc.text(`Última atualização: ${horaRodou}`, 600, 70);

    // Mapeia os códigos d7, d6... para as datas reais
    const mapaCodigosParaData = {};
    periodosOrdenados.forEach((codigo, index) => {
      if (periodos[index]) {
        mapaCodigosParaData[codigo] = periodos[index].DATA;
      }
    });
    mapaCodigosParaData['semana'] = 'Semana';
    mapaCodigosParaData['mes'] = 'Mês';
    mapaCodigosParaData['safra'] = 'Safra';


    // Cabeçalhos
    const headers = [
      "Período",
      ...periodosFiltrados.map(p => mapaCodigosParaData[p] || p.toUpperCase())
    ];



    // Corpo
    const body = [];

    const montarLinha = (titulo, dados) => {
      const linha = [titulo];
      periodosFiltrados.forEach(periodo => {
        const item = dados.find(d => d.PERIODO === periodo);
        linha.push(item && item.VALOR != 0 ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "0");
      });
      body.push(linha);
    };

    // Linhas da tabela
    montarLinha("ATR (kg/ton)", atr);
    montarLinha("ATR Cooperval (kg/ton)", atrCooperval);
    montarLinha("ATR Parceiro (kg/ton)", atrParceiro);
    montarLinha("Fibra Cana (%)", fibraCana);
    montarLinha("Pureza (%)", pureza);
    montarLinha("Impureza Mineral Geral (kg/ton)", impureza);
    montarLinha("Impureza Mineral Manual (kg/ton)", impurezaManual);
    montarLinha("Impureza Mineral Mecanizada (kg/ton)", impurezaMecanizada);
    montarLinha("Impureza Vegetal Geral (kg/ton)", impurezaVegetalGeral);
    montarLinha("Impureza Vegetal Manual (kg/ton)", impurezaVegetalManual);
    montarLinha("Impureza Vegetal Mecanizada (kg/ton)", impurezaVegetalMecanizada);

    montarLinha("AR Cana (%)", arCana);
    montarLinha("Brix da Cana (%)", brixCana);
    montarLinha("PCC (%)", pcc);



    // Tabela com autoTable
    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 80,
      margin: { top: 50, left: 20, right: 20 },
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [7, 99, 51],
        textColor: 255
      },
      didDrawPage: () => {
        doc.setGState(new doc.GState({ opacity: 0.08 }));
        doc.addImage(marcaDaguaBase64, 'JPEG', centerX, centerY, marcaW, marcaH);
        doc.setGState(new doc.GState({ opacity: 1 }));
      }
    });

    doc.save(`boletim_qualidade_${new Date().toLocaleString("pt-BR")}.pdf`);
  };



  //===========================TELA CHEIA==============================//
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log(`Error attempting to enable full - screen mode: ${err.message}(${err.name})`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };






  return (
    <>

      <Geral>
        <div>

          <Table>
            <Thead>
              <Tr noHover>
                <CabecalhoTabela colSpan={13}>
                  <HeaderWrapper>
                    <h2 >Qualidade</h2>
                    <HeaderRight>
                      <p >Atualizado: {horaRodou}</p>
                      <IconGroup>
                        <Button onClick={gerarPdfRelatorio} placeholder="Imprimir">
                          <FaDownload />
                        </Button>
                        <Button
                          onClick={zoomOut}
                          title="Diminuir zoom (Alt+-)"
                          aria-label="Diminuir zoom"
                          type="button"
                        >
                          <FiZoomOut />
                        </Button>
                        <Button
                          onClick={zoomIn}
                          title="Aumentar zoom (Alt+=)"
                          aria-label="Aumentar zoom"
                          type="button"
                        >
                          <FiZoomIn />
                        </Button>
                      </IconGroup>
                    </HeaderRight>
                  </HeaderWrapper>
                </CabecalhoTabela>

              </Tr>


              <Tr noHover>

                <Th width="22%" >Período:</Th>


                {Array.isArray(periodos) &&
                  periodos
                    .map((item, index) => ({
                      ...item,
                      codigo: codigosPeriodos[index] // adiciona código associado à posição
                    }))
                    .filter(p => !isMobile || periodosMobile.includes(p.codigo))
                    .map((item, index) => (
                      <Th key={index}>
                        <div className="tooltip">
                          {item.DATA}
                          <span className="tooltiptext">{item.PERIODO}</span>
                        </div>
                      </Th>
                    ))
                }


              </Tr>
            </Thead>

            <Tbody>
              <LinhaTabela titulo="ATR (kg/ton)" dados2={atr} />
              <LinhaTabela titulo="ATR Cooperval (kg/ton)" dados2={atrCooperval} />
              <LinhaTabela titulo="ATR Parceiro (kg/ton)" dados2={atrParceiro} />
              <LinhaTabela titulo="Fibra Cana (%)" dados2={fibraCana} />
              <LinhaTabela titulo="Pureza (%)" dados2={pureza} />
              <LinhaTabela titulo="Impureza Mineral Geral (kg/ton)" dados2={impureza} />
              <LinhaTabela titulo="Impureza Mineral Manual (kg/ton)" dados2={impurezaManual} />
              <LinhaTabela titulo="Impureza Mineral Mecanizada (kg/ton)" dados2={impurezaMecanizada} />
              <LinhaTabela titulo="Impureza Vegetal Geral (kg/ton)" dados2={impurezaVegetalGeral} />
              <LinhaTabela titulo="Impureza Vegetal Manual (kg/ton)" dados2={impurezaVegetalManual} />
              <LinhaTabela titulo="Impureza Vegetal Mecanizada (kg/ton)" dados2={impurezaVegetalMecanizada} />
              <LinhaTabela titulo="AR Cana (%)" dados2={arCana} />
              <LinhaTabela titulo="Brix da Cana (%)" dados2={brixCana} />
              <LinhaTabela titulo="PCC (%)" dados2={pcc} />

            </Tbody>
          </Table>


        </div>

      </Geral >

    </>

  );
};



export default Grid;
